/**
 * What to ask Led, given what you are looking at.
 *
 * ## Why the console writes these rather than the backend
 *
 * `/api/ask/suggestions` already exists and is context-aware — it is the same
 * list mobile Ask Led shows, and it knows things this module never will (your
 * recent walks, what the assistant is good at answering today). What it does
 * NOT know is which console page you are on, because that is a fact about a
 * URL that never leaves the browser. A suggestion strip that offered the same
 * four questions on the punch list, on a walk and on the daily log would be
 * decoration; the point of putting it above the composer is that it changes
 * as you move.
 *
 * So the page is answered here, and the server's list is the FALLBACK for
 * pages that have nothing specific to ask — plus the built-in four when the
 * gateway is unconfigured and the server has nothing either. Three tiers, each
 * one a real answer rather than a placeholder.
 *
 * ## They are questions, not commands
 *
 * Every string here is something a superintendent would actually type. None of
 * them mutate: `askChatPolicy`'s `isMutatingSuggestion` governs what the Ask
 * tab is allowed to act on, and a strip that offered "close this punch item"
 * from a page with no confirmation step would be offering to do something the
 * reader cannot see the consequences of.
 */

/** The built-ins, used when neither the page nor the server has anything —
 *  the same four `AskLed` has always shipped. */
export const GENERIC_SUGGESTIONS: readonly string[] = [
  "What happened on my most recent walk?",
  "What tasks are still open this week?",
  "Summarize this week's site activity.",
  "Any safety issues flagged recently?",
];

/** How many pills the strip shows. Two, because they sit on ONE line above a
 *  composer and a third would either wrap or squeeze the other two to
 *  ellipsis — and a suggestion you cannot read is not a suggestion. */
export const SUGGESTION_LIMIT = 2;

/**
 * What the reader is looking at, reduced to the only thing this module needs.
 *
 * Deliberately not the raw `search` string: the caller already parses the URL
 * to decide what to render, and re-parsing it here would make the strip's
 * idea of "which page" drift from the shell's the first time a parameter is
 * renamed.
 */
export interface AskPageContext {
  /** The shell's `page`, e.g. `items`, `walks`, `review`, `brief`. */
  page: string;
  /** The saved view when the item list is filtered to one kind (`punch`,
   *  `task`, `obs`, `log`) — the list's own `?view=`. */
  view?: string | null;
  /** Set when a single record is open. */
  itemOpen?: boolean;
  /** Set when a single walk is open. */
  walkOpen?: boolean;
  /** Set when a project is open. */
  projectOpen?: boolean;
  /** The project's name, when one is in scope and known — a question that
   *  names the site reads as being about THIS site rather than about sites. */
  projectName?: string | null;
}

const BY_VIEW: Record<string, string[]> = {
  punch: [
    "Which punch items have been open longest?",
    "Who has the most punch items assigned?",
  ],
  task: ["Which tasks are overdue?", "What tasks did we open this week?"],
  obs: [
    "What observations came up most recently?",
    "Any observations that look like defects?",
  ],
  log: ["What was logged on site this week?", "Which days had no daily log?"],
};

const BY_PAGE: Record<string, string[]> = {
  items: ["What needs my attention right now?", "What has been open the longest?"],
  walks: ["What happened on my most recent walk?", "Which walks still need review?"],
  review: ["What is waiting on my review?", "Which walk has the most left to review?"],
  brief: ["What needs my attention today?", "What has been sitting the longest?"],
  people: ["Who has been on site the most this week?", "Who has the most open work?"],
  policies: ["Which record types are we capturing?", "What changed in our settings recently?"],
};

/**
 * Whether this page answers for itself.
 *
 * The composer asks BEFORE fetching: the server's list is only ever the
 * fallback, so requesting it for a page that already has its own questions is
 * a round trip whose result is discarded. It also keeps the console's request
 * count honest — the shell's tests count every fetch, and a convenience call
 * that fires on pages that never use it is a call that should not exist.
 */
export function hasPageSuggestions(context: AskPageContext): boolean {
  return pageSpecific(context).length > 0;
}

/**
 * The strip's questions for this page, best source first.
 *
 * `serverSuggestions` is what `/api/ask/suggestions` returned (empty when the
 * gateway is unconfigured or the call failed — it is a convenience, and its
 * own route says a failure must not block the composer).
 */
export function suggestionsFor(
  context: AskPageContext,
  serverSuggestions: readonly string[] = [],
): string[] {
  const specific = pageSpecific(context);
  const chosen = specific.length > 0 ? specific : [...serverSuggestions];
  const filled = chosen.length > 0 ? chosen : [...GENERIC_SUGGESTIONS];
  return filled.slice(0, SUGGESTION_LIMIT);
}

function pageSpecific(context: AskPageContext): string[] {
  // One record open beats everything else on screen: the reader is looking at
  // a thing, not at a list of things.
  if (context.itemOpen) {
    return ["What is the history on this item?", "Who is this waiting on?"];
  }
  if (context.walkOpen) {
    return ["Summarize this walk.", "What needs follow-up from this walk?"];
  }
  if (context.projectOpen) {
    const site = context.projectName?.trim();
    return site
      ? [`What is open at ${site}?`, `What happened at ${site} this week?`]
      : ["What is open at this project?", "What happened here this week?"];
  }
  // A filtered item list asks about its KIND — this is the case the strip
  // exists for, and the one a server that cannot see the URL cannot answer.
  if (context.page === "items" && context.view && BY_VIEW[context.view]) {
    return [...BY_VIEW[context.view]!];
  }
  return BY_PAGE[context.page] ? [...BY_PAGE[context.page]!] : [];
}
